// Cookie 键名
const COOKIE_USER_RANDOM_ID = 'user_random_id';

// 缓存键名
const FINGERPRINT_CACHE_KEY = '__fingerprint_cache__';
// 缓存版本号，用于在算法更新时自动失效旧缓存
const FINGERPRINT_CACHE_VERSION = '1.0.0';
// 缓存过期时间（毫秒），30天
const FINGERPRINT_CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000;

// 简单的 Cookie 操作函数
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function setCookie(name: string, value: string, days = 365): void {
  if (typeof document === 'undefined') return;
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`;
}

// 缓存数据结构
interface FingerprintCache {
  fingerprint: string;
  version: string;
  timestamp: number;
}

// 安全地获取 localStorage
function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// 从缓存中获取指纹
function getCachedFingerprint(): string | null {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const cached = storage.getItem(FINGERPRINT_CACHE_KEY);
    if (!cached) return null;

    const cache: FingerprintCache = JSON.parse(cached);

    // 检查版本号
    if (cache.version !== FINGERPRINT_CACHE_VERSION) {
      storage.removeItem(FINGERPRINT_CACHE_KEY);
      return null;
    }

    // 检查是否过期
    const now = Date.now();
    if (now - cache.timestamp > FINGERPRINT_CACHE_EXPIRY) {
      storage.removeItem(FINGERPRINT_CACHE_KEY);
      return null;
    }

    // 判断 cookie 中是否存在用户随机 id,不存在则设置
    const userRandomId = getCookie(COOKIE_USER_RANDOM_ID);
    if (!userRandomId) {
      setCookie(COOKIE_USER_RANDOM_ID, cache.fingerprint);
    }

    return cache.fingerprint;
  } catch (_err) {
    // 缓存数据损坏，清除它
    storage.removeItem(FINGERPRINT_CACHE_KEY);
    return null;
  }
}

// 将指纹保存到缓存
function setCachedFingerprint(fingerprint: string): void {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    const cache: FingerprintCache = {
      fingerprint,
      version: FINGERPRINT_CACHE_VERSION,
      timestamp: Date.now(),
    };
    storage.setItem(FINGERPRINT_CACHE_KEY, JSON.stringify(cache));
    setCookie(COOKIE_USER_RANDOM_ID, fingerprint);
  } catch (_err) {
    // 存储失败（可能是存储空间不足或隐私模式），忽略错误
  }
}

export function uuid() {
  let d = new Date().getTime();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function murmurX64Hash128(key: string, seed = 0) {
  // 常量定义（64 位常量拆分为高低 32 位）
  const c1 = [0x87c37b91, 0x114253d5]; // 0x87c37b91114253d5
  const c2 = [0x4cf5ad43, 0x2745937f]; // 0x4cf5ad432745937f
  const m = 5;
  const n = [0x52dce729, 0]; // 0x52dce729

  // 将输入转换为 Uint8Array
  const encoder = new TextEncoder();
  const data = typeof key === 'string' ? encoder.encode(key) : new Uint8Array(key);
  const length = data.length;

  // 初始化 h1 和 h2（拆分为高低 32 位）
  let h1 = [seed >>> 0, 0];
  let h2 = [seed >>> 0, 0];

  // 辅助函数：64 位乘法（模拟）
  function mul64(a: number[], b: number[]) {
    const aLo = a[1] >>> 0,
      aHi = a[0] >>> 0;
    const bLo = b[1] >>> 0,
      bHi = b[0] >>> 0;
    const lo = aLo * bLo;
    const hi = (aHi * bLo + aLo * bHi + (lo >>> 0) / 0x100000000) | 0;
    return [hi >>> 0, lo >>> 0];
  }

  // 辅助函数：64 位左旋转
  function rotl64(x: number[], r: number) {
    const hi = x[0] >>> 0,
      lo = x[1] >>> 0;
    if (r >= 32) {
      return [lo >>> (64 - r), hi >>> (64 - r)];
    }
    const carry = (lo >>> (32 - r)) | (hi << r);
    return [(hi << r) | (lo >>> (32 - r)), (lo << r) | carry];
  }

  // 辅助函数：64 位异或
  function xor64(a: number[], b: number[]) {
    return [a[0] ^ b[0], a[1] ^ b[1]];
  }

  // 辅助函数：64 位加法
  function add64(a: number[], b: number[]) {
    const lo = (a[1] >>> 0) + (b[1] >>> 0);
    const carry = (lo / 0x100000000) | 0;
    const hi = (a[0] >>> 0) + (b[0] >>> 0) + carry;
    return [hi >>> 0, lo >>> 0];
  }

  // 处理 16 字节块
  let i = 0;
  for (; i <= length - 16; i += 16) {
    const part1 = (data[i] | (data[i + 1] << 8) | (data[i + 2] << 16) | (data[i + 3] << 24)) >>> 0;

    const part2 =
      (data[i + 4] | (data[i + 5] << 8) | (data[i + 6] << 16) | (data[i + 7] << 24)) >>> 0;

    const part3 =
      (data[i + 8] | (data[i + 9] << 8) | (data[i + 10] << 16) | (data[i + 11] << 24)) >>> 0;

    const part4 =
      (data[i + 12] | (data[i + 13] << 8) | (data[i + 14] << 16) | (data[i + 15] << 24)) >>> 0;

    const k1 = [part1, part2];
    const k2 = [part3, part4];

    // k1 处理
    let t = mul64(k1, c1);
    t = rotl64(t, 31);
    t = mul64(t, c2);
    h1 = xor64(h1, t);
    h1 = rotl64(h1, 27);
    h1 = add64(mul64(h1, [0, m]), n);

    // k2 处理
    t = mul64(k2, c2);
    t = rotl64(t, 33);
    t = mul64(t, c1);
    h2 = xor64(h2, t);
    h2 = rotl64(h2, 31);
    h2 = add64(mul64(h2, [0, m]), [0x38495ab5, 0]);
  }

  // 处理尾部
  let k1 = [0, 0],
    k2 = [0, 0];
  switch (length - i) {
    case 15:
      k2[1] ^= data[i + 14] << 16;
      break;
    case 14:
      k2[1] ^= data[i + 13] << 8;
      break;
    case 13:
      k2[1] ^= data[i + 12];
      break;
    case 12:
      k2[0] ^= data[i + 11] << 24;
      break;
    case 11:
      k2[0] ^= data[i + 10] << 16;
      break;
    case 10:
      k2[0] ^= data[i + 9] << 8;
      break;
    case 9:
      k2[0] ^= data[i + 8];
      break;
    case 8:
      k1[1] ^= data[i + 7] << 24;
      break;
    case 7:
      k1[1] ^= data[i + 6] << 16;
      break;
    case 6:
      k1[1] ^= data[i + 5] << 8;
      break;
    case 5:
      k1[1] ^= data[i + 4];
      break;
    case 4:
      k1[0] ^= data[i + 3] << 24;
      break;
    case 3:
      k1[0] ^= data[i + 2] << 16;
      break;
    case 2:
      k1[0] ^= data[i + 1] << 8;
      break;
    case 1:
      k1[0] ^= data[i];
  }

  // 尾部混合
  k1 = mul64(mul64(k1, c1), c2);
  h1 = xor64(h1, k1);
  k2 = mul64(mul64(k2, c2), c1);
  h2 = xor64(h2, k2);

  // 最终化
  h1 = xor64(h1, [0, length]);
  h2 = xor64(h2, [0, length]);
  h1 = add64(h1, h2);
  h2 = add64(h2, h1);

  for (let j = 0; j < 2; j++) {
    h1 = mul64(xor64(h1, [h1[0] >>> 1, h1[1] >>> 1]), [0xff51afd7, 0xed558ccd]);
    h2 = mul64(xor64(h2, [h2[0] >>> 1, h2[1] >>> 1]), [0xc4ceb9fe, 0x1a85ec53]);
    h1 = xor64(h1, [h1[0] >>> 1, h1[1] >>> 1]);
    h2 = xor64(h2, [h2[0] >>> 1, h2[1] >>> 1]);
  }

  h1 = add64(h1, h2);
  h2 = add64(h2, h1);

  // 返回 128 位哈希值（16 字节十六进制字符串）
  return (
    (h1[0] >>> 0).toString(16).padStart(8, '0') +
    (h1[1] >>> 0).toString(16).padStart(8, '0') +
    (h2[0] >>> 0).toString(16).padStart(8, '0') +
    (h2[1] >>> 0).toString(16).padStart(8, '0')
  );
}

// 获取 Canvas 指纹
function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.textBaseline = 'top';
    ctx.font = '16px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('fingerprint!', 2, 15);
    return canvas.toDataURL();
  } catch (_err) {
    return 'unknown';
  }
}
// 获取 audio 指纹
async function getAudioFingerprint() {
  return new Promise<string>(resolve => {
    const AudioContext =
      window.OfflineAudioContext ||
      (window as { webkitOfflineAudioContext?: typeof OfflineAudioContext })
        .webkitOfflineAudioContext;
    if (!AudioContext) {
      resolve('unknown');
      return;
    }
    const ctx = new AudioContext(1, 44100, 44100);
    const oscillator = ctx.createOscillator();
    const analyser = ctx.createAnalyser();
    const gain = ctx.createGain();
    const scriptProcessor = ctx.createScriptProcessor(4096, 1, 1);

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(10000, ctx.currentTime);
    gain.gain.setValueAtTime(0, ctx.currentTime); // Mute output

    oscillator.connect(gain);
    gain.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(ctx.destination);

    const fingerprintData: number[] = [];

    scriptProcessor.onaudioprocess = () => {
      const data = new Float32Array(analyser.frequencyBinCount);
      analyser.getFloatFrequencyData(data);
      fingerprintData.push(...data.slice(0, 10));
    };

    oscillator.start(0);
    ctx.startRendering().then(() => {
      oscillator.stop();
      resolve(fingerprintData.slice(0, 10).join(','));
    });
  });
}

function getFontFingerprint() {
  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testFonts = [
    'Arial',
    'Verdana',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Comic Sans MS',
    'Trebuchet MS',
    'Impact',
  ];

  const testString = 'fingerprint';
  const testSize = '72px';
  const span = document.createElement('span');
  span.style.position = 'absolute';
  span.style.left = '-9999px';
  span.style.fontSize = testSize;
  span.textContent = testString;
  document.body.appendChild(span);

  const defaultWidths: Record<string, number> = {};
  baseFonts.forEach(function (base) {
    span.style.fontFamily = base;
    defaultWidths[base] = span.offsetWidth;
  });

  const detected: string[] = [];
  testFonts.forEach(function (font) {
    span.style.fontFamily = `${font}, monospace`;
    if (span.offsetWidth !== defaultWidths['monospace']) {
      detected.push(font);
    }
  });

  document.body.removeChild(span);
  return detected.join(',');
}

// 获取 WebGL Vendor 和 Version（更稳定的 WebGL 指纹）
function getWebGLVendor() {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return 'unknown';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown';
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';
      return `${vendor}|${renderer}`;
    }
    return gl.getParameter(gl.VENDOR) || 'unknown';
  } catch (_err) {
    return 'unknown';
  }
}

// 收集指纹组件（优化版本：移除不稳定的组件，添加更多稳定组件）
async function collectFingerprintComponents() {
  const components: (string | number)[] = [];

  // 1. 浏览器基础信息（稳定）
  components.push(navigator.userAgent || 'unknown');
  components.push(navigator.language || 'unknown');
  components.push(navigator.platform || 'unknown');

  // 2. 硬件信息（稳定）
  components.push(navigator.hardwareConcurrency || 0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components.push((navigator as any).deviceMemory || 0);
  components.push(screen.colorDepth || 0);
  components.push(screen.pixelDepth || 0);

  // 3. 时区信息（稳定）
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
    components.push(timeZone);
  } catch (_err) {
    components.push('unknown');
  }

  // 4. Canvas 指纹（相对稳定，但可能因浏览器/驱动更新而变化）
  try {
    components.push(getCanvasFingerprint());
  } catch (_err) {
    components.push('unknown');
  }

  // 5. WebGL 指纹（相对稳定）
  try {
    const webglInfo = getWebGLVendor();
    components.push(webglInfo);
  } catch (_err) {
    components.push('unknown');
  }

  // 6. Audio 指纹（相对稳定）
  try {
    const audioFingerprint = await getAudioFingerprint();
    components.push(String(audioFingerprint));
  } catch (_err) {
    components.push('unknown');
  }

  // 7. 字体指纹（可能因系统字体变化而变化，但变化频率低）
  try {
    components.push(getFontFingerprint());
  } catch (_err) {
    components.push('unknown');
  }

  // 8. 其他稳定特征
  try {
    // 支持的媒体类型
    const mediaTypes = ['video', 'audio'].map(type => {
      const element = document.createElement(type) as HTMLVideoElement | HTMLAudioElement;
      const canPlay = element.canPlayType ? element.canPlayType('video/mp4') || 'no' : 'no';
      return `${type}:${canPlay}`;
    });
    components.push(mediaTypes.join('|'));
  } catch (_err) {
    components.push('unknown');
  }

  // 9. 浏览器特性检测
  try {
    const features = [
      'localStorage' in window ? '1' : '0',
      'sessionStorage' in window ? '1' : '0',
      'indexedDB' in window ? '1' : '0',
      'serviceWorker' in navigator ? '1' : '0',
      'geolocation' in navigator ? '1' : '0',
    ];
    components.push(features.join(''));
  } catch (_err) {
    components.push('unknown');
  }

  return components;
}

// 生成指纹（使用 murmurX64Hash128）
async function generateFingerprint() {
  const components = await collectFingerprintComponents();
  // 使用固定分隔符和 seed，确保结果可复现
  const fingerprint = murmurX64Hash128(components.join('###'), 12345);
  return `${fingerprint}-stable`;
}

// 获取指纹（主函数，完全剥离 fingerprintjs，带缓存）
export const getFingerprint = async (options?: {
  /** 是否强制重新生成，忽略缓存 */
  force?: boolean;
}): Promise<string> => {
  // 1. 检查缓存（如果未强制刷新）
  if (!options?.force) {
    const cached = getCachedFingerprint();
    if (cached) {
      return cached;
    }
  }

  // 2. 生成新指纹
  try {
    const fingerprint = await generateFingerprint();
    // 3. 保存到缓存
    setCachedFingerprint(fingerprint);
    return fingerprint;
  } catch (_err) {
    // 如果自定义方案失败，降级到 UUID
    const fallbackFingerprint = `${uuid()}-type3`;
    // UUID 也尝试缓存，避免每次失败都生成新的 UUID
    setCachedFingerprint(fallbackFingerprint);
    return fallbackFingerprint;
  }
};
